import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailTrolley } from "../../../../../http/hrail/types/trolleys";
import { useHrailTrolleysApi } from "../../../../../http/hrail/useTrolleys";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateTrolleyModalDialogButton from "./CreateTrolleyModalDialogButton";
import TrolleyForm from "./TrolleyForm";

type Props = {
  active?: boolean;
};

const Trolleys: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "trolleys" });
  const aborter = useRef(new AbortController());
  const trolleyApi = useHrailTrolleysApi();
  const [itemToEdit, setItemToEdit] = useState<HrailTrolley>();
  const [itemToDelete, setItemToDelete] = useState<HrailTrolley>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "trolleys");
  const isActiveTab = active ?? true;
  const updateVisibility = async (trolley: HrailTrolley, visibility: VisibilityScopeValue) => {
    try {
      await trolleyApi.updateAdminHrailTrolleyVisibility(trolley.item.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (trolley: HrailTrolley, newOrder: number) => {
    try {
      await trolleyApi.updateAdminHrailTrolleyOrder(trolley.item.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("hrail.trolley");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = trolleyApi.useAdminHrailTrolleys(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.trolleys.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.trolleys.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateTrolleyModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailTrolley>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.description}</th>
            <th align="left">{i18n.translation.systems.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(trolley) => (
          <>
            <Table.Td noWrap={true}>{trolley.item?.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{trolley.item?.description ?? ""}</Table.Td>
            <td>{trolley.systems?.map((system) => system.name).join("; ")}</td>
            <td align="center">
              <Visibility value={trolley.visibility} onChange={(value) => updateVisibility(trolley, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={trolley.order} onUpdate={(newOrder) => updateOrder(trolley, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(trolley) => setItemToEdit(trolley)}
        rowActions={(trolley) => [
          getEditAction(i18n, () => setItemToEdit(trolley)),
          accessesAction(trolley.item),
          getDeleteAction(i18n, () => setItemToDelete(trolley), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.trolleys.edit}>
          <TrolleyForm trolley={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.trolleys.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                trolleyApi
                  .deleteAdminHrailTrolley(itemToDelete.item.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.trolleys.deleteLabel,
            [itemToDelete.item?.code ?? "", itemToDelete.item?.description ?? ""].filter(Boolean).join(" - ") ||
              itemToDelete.item.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default Trolleys;
